import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '@clerk/react'
import { fetchDigestSetting, setDigestSetting, DigestError, type DigestSetting } from '@/lib/digest'
import { describeDigestSetting } from '../../workers/digest-format'

/**
 * THE WEEKLY REPORT — the owner's switch for the Monday text (Build 9, G).
 *
 * ONE CARD, TWO PRODUCTS (Build 10e). Moved here whole from
 * src/routes/Dashboard.tsx, not copied, when Kelvo's Report page needed the
 * same switch (§20.15): Foredesk's dashboard renders it exactly as before,
 * with no props, and Kelvo's /report renders the same component. The engine
 * decides what each owner sees (GET /api/admin/digest), so a Kelvo pod not yet
 * switched on reads "Not switched on" with no button, as it should.
 *
 * THIS COMPONENT CHOOSES NO WORDS. Every state's status, sentence, note and
 * button come from describeDigestSetting() in workers/digest-format.ts, which
 * has a suite — the same rule as the counters above. Four states, and only the
 * two he controls (on, off) offer a button; "not switched on for your account"
 * and "no mobile" say so plainly with nothing to press.
 *
 * THE CARD DRAWS WHAT THE SERVER SAYS. After a press it shows the state the
 * engine answered, never the state the button hoped for, so a refused or
 * failed change can never leave "Off" on the screen over a digest still coming.
 *
 * Non-blocking like the counters: a failure collapses to one line with a retry.
 */
export default function WeeklyReportCard() {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const [setting, setSetting] = useState<DigestSetting | null>(null)
  const [state, setState] = useState<'loading' | 'loaded' | 'error'>('loading')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setState('loading')
    setError('')
    try {
      setSetting(await fetchDigestSetting(await getTokenRef.current()))
      setState('loaded')
    } catch (e) {
      setError(e instanceof DigestError ? e.message : 'Could not load your weekly report setting.')
      setState('error')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function handleSwitch(on: boolean) {
    setSaving(true)
    setError('')
    try {
      setSetting(await setDigestSetting(await getTokenRef.current(), on))
    } catch (e) {
      setError(e instanceof DigestError ? e.message : 'Could not save that. Nothing was changed.')
    } finally {
      setSaving(false)
    }
  }

  if (state === 'loading') {
    return <div className="mb-6 h-20 animate-pulse rounded-card border border-border-subtle bg-surface-raised" />
  }

  if (state === 'error' || !setting) {
    return (
      <div className="mb-6 rounded-card border border-border-subtle bg-surface-raised px-4 py-3 text-sm text-text-danger">
        {error || 'Could not load your weekly report setting.'}{' '}
        <button onClick={() => void load()} className="underline underline-offset-2">
          Try again
        </button>
      </div>
    )
  }

  const card = describeDigestSetting(setting)

  return (
    <section className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-card border border-border-subtle bg-surface-raised px-4 py-4">
      <div className="min-w-0 flex-1">
        <h2 className="font-display text-sm font-semibold tracking-tight text-text-primary">
          Weekly report <span className="font-normal text-text-muted">· {card.status}</span>
        </h2>
        <p className="mt-1 text-sm text-text-primary">{card.body}</p>
        {card.note ? <p className="mt-1 text-xs text-text-muted">{card.note}</p> : null}
        {error ? (
          <p role="alert" className="mt-1 text-sm text-text-danger">
            {error}
          </p>
        ) : null}
      </div>
      {card.action ? (
        <button
          onClick={() => void handleSwitch(card.action!.turnOn)}
          disabled={saving}
          className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-primary transition-colors hover:bg-surface-base disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
        >
          {saving ? 'Saving…' : card.action.label}
        </button>
      ) : null}
    </section>
  )
}
